/**
 * Componente de tabla para mostrar y gestionar usuarios en el panel admin
 * Incluye paginación, búsqueda y columnas para acciones futuras
 * Usa @tanstack/react-table para gestión eficiente de datos
 */

import React, { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';
import { RiKeyLine } from 'react-icons/ri';
import UserChangePassword from './UserChangePassword';
import { FiRefreshCw } from 'react-icons/fi';
import MegaCard, { MegaCardFooter, MegaCardHead } from '../../features/styled-components/Card/MegaCard';
import { PiArrowLeftBold, PiArrowRightBold, PiUsersDuotone } from 'react-icons/pi';

/**
 * Formatea fecha para mostrar en formato legible
 * @param {string} dateString - Fecha en formato ISO
 * @returns {string} - Fecha formateada
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formatea fecha para mostrar tiempo relativo (ej: "hace 2 horas", "hace 3 días")
 * @param {string} dateString - Fecha en formato ISO
 * @returns {string} - Tiempo relativo formateado
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) {
    return 'ahora mismo';
  } else if (diffMinutes < 60) {
    return `hace ${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    return `hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
  } else if (diffDays < 7) {
    return `hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
  } else if (diffWeeks < 4) {
    return `hace ${diffWeeks} semana${diffWeeks !== 1 ? 's' : ''}`;
  } else if (diffMonths < 12) {
    return `hace ${diffMonths} mes${diffMonths !== 1 ? 'es' : ''}`;
  } else {
    return `hace ${diffYears} año${diffYears !== 1 ? 's' : ''}`;
  }
};

// Crea helper para definir columnas de la tabla
const columnHelper = createColumnHelper();

/**
 * Componente UsersTable
 * @param {object} props - Propiedades del componente
 * @param {Array} props.users - Lista de usuarios a mostrar
 * @param {boolean} props.isLoading - Estado de carga
 * @param {boolean} props.isError - Estado de error
 * @param {object} props.error - Error si existe
 * @param {boolean} props.hasNextPage - Si hay más páginas
 * @param {number} props.totalCount - Total de usuarios
 * @param {string} props.searchText - Texto de búsqueda actual
 * @param {string} props.orderBy - Campo de ordenamiento actual
 * @param {string} props.cursor - Cursor actual para paginación
 * @param {Function} props.onLoadNextPage - Función para cargar siguiente página
 * @param {Function} props.onSearchChange - Función para cambiar búsqueda
 * @param {Function} props.onOrderByChange - Función para cambiar ordenamiento
 * @param {Function} props.onResetPage - Función para resetear a primera página
 * @param {Function} props.onRefetch - Función para refrescar los datos
 */
const UsersTable = ({
  users = [],
  isLoading = false,
  isError = false,
  error = null,
  hasNextPage = false,
  totalCount = 0,
  searchText = '',
  orderBy = 'updated_at',
  cursor = null,
  onLoadNextPage,
  onSearchChange,
  onOrderByChange,
  onResetPage,
  onRefetch
}) => {
  // Estado para controlar el modal de cambio de contraseña
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Abre el modal de cambio de contraseña para un usuario específico.
  const handleOpenPasswordModal = (user) => {
    setSelectedUser(user);
    setIsPasswordModalOpen(true);
  };

  // Cierra el modal de cambio de contraseña.
  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setSelectedUser(null);
  };

  // Refresca los datos de la tabla.
  // Llama a la función onRefetch para volver a obtener los usuarios.
  const handleRefetch = () => {
    if (onRefetch) {
      onRefetch();
    }
  };
  // Define las columnas de la tabla usando tanstack/react-table
  const columns = React.useMemo(() => [
    columnHelper.accessor('id', {
      header: 'UUID',
      cell: info => info.getValue()?.slice(0, 8) + '...'
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor('username', {
      header: 'Username',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor('full_name', {
      header: 'Nombre completo',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor('years', {
      header: 'Edad',
      cell: info => info.getValue() || '-'
    }),
    columnHelper.accessor('sex', {
      header: 'Sexo',
      cell: info => {
        const sex = info.getValue();
        // 1 = Mujer, 2 = Hombre
        return sex === 1 ? 'Mujer' : sex === 2 ? 'Hombre' : '-';
      }
    }),
    columnHelper.accessor('sex_pref', {
      header: 'Busca',
      cell: info => {
        const sexPref = info.getValue();
        // 0 = Ambos, 1 = Mujeres, 2 = Hombres
        return sexPref === 0 ? 'Ambos' : sexPref === 1 ? 'Mujeres' : sexPref === 2 ? 'Hombres' : '-';
      }
    }),
    columnHelper.accessor('created_at', {
      header: 'Creado',
      cell: info => formatDate(info.getValue())
    }),
    columnHelper.accessor('last_sign_in_at', {
      header: 'Última conexión',
      cell: info => formatRelativeTime(info.getValue())
    }),
    columnHelper.display({
      id: 'acciones',
      header: 'Acciones',
      cell: info => {
        const user = info.row.original;
        return (
          <div className="flex gap-2">
            <button
              className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              disabled
              title="Próximamente"
            >
              Ver
            </button>
            <button
              className="px-3 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
              disabled
              title="Próximamente"
            >
              Editar
            </button>
            <button
              className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1"
              onClick={() => handleOpenPasswordModal(user)}
              title="Cambiar contraseña"
            >
              <RiKeyLine size={14} />
              Contraseña
            </button>
          </div>
        );
      }
    })
  ], []);

  // Crea la instancia de la tabla con tanstack/react-table
  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  // Opciones de ordenamiento
  const orderByOptions = [
    { value: 'updated_at', label: 'Última actualización' },
    { value: 'created_at', label: 'Fecha de creación' },
    { value: 'email', label: 'Email' }
  ];

  // Manejo de cambio en búsqueda
  const handleSearchInputChange = (e) => {
    onSearchChange(e.target.value);
  };

  // Manejo de cambio en ordenamiento
  const handleOrderByChange = (e) => {
    onOrderByChange(e.target.value);
  };

  return (
    <MegaCard>
      {/* Header con controles de búsqueda y ordenamiento */}
      <MegaCardHead otraClassname="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Buscar por nombre, username o email..."
            value={searchText}
            onChange={handleSearchInputChange}
            className="px-4 py-2 border border-neutral-600 light:border-neutral-300 bg-neutral-800 light:bg-white text-neutral-100 light:text-neutral-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 light:focus:ring-blue-500"
          />
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={orderBy}
            onChange={handleOrderByChange}
            className="px-4 py-2 border border-neutral-600 light:border-neutral-300 bg-neutral-800 light:bg-white text-neutral-100 light:text-neutral-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 light:focus:ring-blue-500"
          >
            {orderByOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

        <button
          onClick={handleRefetch}
          disabled={isLoading}
          className="p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refrescar datos"
        >
          <FiRefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
        </button>

        </div>
      </MegaCardHead>

      {/* Estado de carga */}
      {isLoading && users.length === 0 && (
        <div className="text-center py-8 text-neutral-400 light:text-neutral-600">
          Cargando usuarios...
        </div>
      )}

      {/* Estado de error */}
      {isError && (
        <div className="text-center py-8 text-red-400 light:text-red-600">
          Error: {error?.message || 'Error al cargar usuarios'}
        </div>
      )}

      {/* Tabla de usuarios */}
      {!isLoading && !isError && (
        <>
          {users.length === 0 ? (
            <div className="text-center py-8 text-neutral-400 light:text-neutral-600">
              No se encontraron usuarios
            </div>
          ) : (
            <div className="bg-neutral-900 light:bg-neutral-50 w-full overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto max-h-[calc(100dvh-24rem)] w-full">
                <table className="w-full table-auto min-w-[1200px]">
                  <thead className="sticky top-0 z-10">
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id} className="light:bg-neutral-100">
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            className="px-4 py-3 text-left text-sm font-semibold text-neutral-300 light:text-neutral-700 border-b border-neutral-700 light:border-neutral-200 whitespace-nowrap bg-neutral-900 light:bg-neutral-100"
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row, index) => (
                      <tr
                        key={row.id}
                        className={index % 2 === 0 ? 'bg-neutral-800 light:bg-white' : 'bg-neutral-700 light:bg-neutral-50'}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 text-sm text-neutral-200 light:text-neutral-800 border-b border-neutral-700 light:border-neutral-200 whitespace-nowrap"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <MegaCardFooter>
        {/* Controles de paginación */}
        {!isLoading && !isError && users.length > 0 && (
          <div className="flex justify-between items-center">
            <p className="flex-1 flex items-center gap-2 text-sm text-neutral-400 light:text-neutral-600">
              {users.length} / {totalCount.toLocaleString()} <PiUsersDuotone size={18} />
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={onResetPage}
                disabled={!searchText && !cursor}
                className="px-4 py-2 bg-neutral-700 light:bg-neutral-200 text-neutral-300 light:text-neutral-700 rounded-lg hover:bg-neutral-600 light:hover:bg-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PiArrowLeftBold />
              </button>
              
              <button
                onClick={onLoadNextPage}
                disabled={!hasNextPage || isLoading}
                className="px-4 py-3 bg-blue-600 light:bg-blue-500 text-white rounded-lg hover:bg-blue-700 light:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? <FiRefreshCw className="animate-spin" /> : <PiArrowRightBold />}
              </button>
            </div>
          </div>
        )}

        {/* Indicador de carga al cargar más páginas */}
        {isLoading && users.length > 0 && (
          <div className="mt-4 text-center text-neutral-400 light:text-neutral-600">
            Cargando más usuarios...
          </div>
        )}

        {/* Modal para cambiar contraseña */}
        <UserChangePassword
          isOpen={isPasswordModalOpen}
          onClose={handleClosePasswordModal}
          userId={selectedUser?.id}
          userEmail={selectedUser?.email}
        />
      </MegaCardFooter>
    </MegaCard>
  );
};

export default UsersTable;